"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";

import type { RestaurantSiteConfig } from "@/lib/restaurant-platform";
import type { MarketingHomepageCopy } from "@/lib/marketing-location-copy";

type OwnerLocationsProps = {
  siteConfig: RestaurantSiteConfig;
  homepageCopy: MarketingHomepageCopy;
};

const WEEKLY_HOURS = [
  { day: "Sunday", hours: "10:00 AM - 8:00 PM" },
  { day: "Monday", hours: "4:00 PM - 9:00 PM" },
  { day: "Tuesday", hours: "4:00 PM - 9:00 PM" },
  { day: "Wednesday", hours: "4:00 PM - 9:00 PM" },
  { day: "Thursday", hours: "4:00 PM - 9:00 PM" },
  { day: "Friday", hours: "4:00 PM - 9:00 PM" },
  { day: "Saturday", hours: "4:00 PM - 9:00 PM" },
] as const; // index aligns with Date.getDay()

const SHORT_DAYS = [
  "Sun",
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
] as const;

const LOCATIONS_MAP_IMAGE =
  "/images/tuscan-cove-wayfinding-map-integrated.png";

const PHONE_DISPLAY = "(725) 223-0714";

function phoneHref(phone: string): string {
  // siteConfig.contact.phone is "+1-725-223-0714"; tel: wants digits + leading "+".
  const digits = phone.replace(/[^\d+]/g, "");
  return `tel:${digits}`;
}

// Resolve the weekday in restaurant time (Pacific) on both server and client so
// the "Today" row matches the restaurant's real day and never hydration-mismatches.
// Server runs UTC, so a plain new Date().getDay() would render tomorrow's hours
// every evening 5 PM to midnight Pacific, then flicker correct on hydration.
function restaurantTodayIndex(): number {
  const shortDay = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Los_Angeles",
    weekday: "short",
  }).format(new Date());
  const index = SHORT_DAYS.indexOf(shortDay as (typeof SHORT_DAYS)[number]);
  return index === -1 ? 0 : index;
}

export function OwnerLocations({
  siteConfig,
  homepageCopy,
}: OwnerLocationsProps): React.JSX.Element {
  const [showHours, setShowHours] = useState(false);

  const mapsHref = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    `${siteConfig.name} ${siteConfig.address.line1} ${siteConfig.address.city} ${siteConfig.address.state} ${siteConfig.address.zip}`,
  )}`;

  const todayIndex = restaurantTodayIndex();
  const todayEntry = WEEKLY_HOURS[todayIndex] ?? WEEKLY_HOURS[0];
  const todayLabel = `${SHORT_DAYS[todayIndex] ?? SHORT_DAYS[0]} ${todayEntry.hours}`;

  const telHref = phoneHref(siteConfig.contact.phone);
  const mailHref = `mailto:${siteConfig.contact.email}`;

  return (
    <section
      aria-labelledby="home-locations-heading"
      className="bg-white px-4 py-14 font-system md:px-6 md:py-20"
    >
      <div className="mx-auto max-w-7xl">
        <h2
          id="home-locations-heading"
          className="mb-6 font-system text-[clamp(1.625rem,4vw,2rem)] font-bold leading-tight tracking-tight text-[#1a1a1a] sm:mb-8"
        >
          Our location
        </h2>

        <div className="overflow-hidden rounded-2xl bg-neutral-50 lg:grid lg:grid-cols-[minmax(0,460px)_1fr]">
          <Link
            href={mapsHref}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open Tuscan Cove in Google Maps"
            data-track="directions_clicked"
            data-track-location="home_locations_directions"
            className="group relative block aspect-[3/2] overflow-hidden rounded-t-2xl lg:aspect-auto lg:h-full lg:min-h-[360px] lg:rounded-l-2xl lg:rounded-tr-none"
          >
            <Image
              src={LOCATIONS_MAP_IMAGE}
              alt={homepageCopy.visitMapAlt}
              fill
              sizes="(max-width: 1023px) 100vw, 460px"
              className="object-cover transition-transform duration-500 group-hover:scale-[1.01]"
            />
          </Link>

          <div className="flex flex-col gap-5 p-6 sm:p-8">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-[1.05rem] font-semibold leading-tight text-[#1a1a1a]">
                  {siteConfig.name}
                </h3>
                <span className="mt-0.5 block text-[1.05rem] font-semibold text-[#1a1a1a]">
                  Southern Highlands, Las Vegas, NV
                </span>
              </div>

              <Link
                href={mapsHref}
                target="_blank"
                rel="noopener noreferrer"
                data-track="directions_clicked"
                data-track-location="home_locations_directions"
                className="hidden items-center rounded-full border border-neutral-300 bg-white px-4 py-2 text-sm font-semibold text-[#1a1a1a] transition-colors hover:bg-neutral-100 lg:inline-flex"
              >
                Get directions
              </Link>
            </div>

            <div className="inline-flex w-fit items-center gap-2 rounded-full bg-[#f45c24]/10 px-3 py-1 text-xs font-semibold text-[#d94e1a]">
              On-site parking
            </div>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  Address
                </p>
                <Link
                  href={mapsHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-track="directions_clicked"
                  data-track-location="home_locations_address"
                  className="block text-sm text-neutral-600 transition-colors hover:text-[#f45c24]"
                >
                  <span className="block">12656 Southern Highlands Pkwy</span>
                  <span className="block">Las Vegas, NV 89141</span>
                </Link>
              </div>

              <div>
                <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-neutral-500">
                  Contacts
                </p>
                <a
                  href={telHref}
                  className="flex min-h-11 w-fit items-center text-sm text-neutral-600 transition-colors hover:text-[#f45c24]"
                >
                  {PHONE_DISPLAY}
                </a>
                <a
                  href={mailHref}
                  className="flex min-h-11 w-fit items-center text-sm text-neutral-600 transition-colors hover:text-[#f45c24]"
                >
                  {siteConfig.contact.email}
                </a>
              </div>
            </div>

            <hr className="border-neutral-200" />

            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm text-neutral-600">Today: {todayLabel}</p>
                <button
                  type="button"
                  onClick={() => setShowHours((prev) => !prev)}
                  aria-expanded={showHours}
                  aria-controls="home-locations-hours"
                  className="inline-flex min-h-11 items-center text-sm font-semibold text-neutral-500 underline-offset-2 transition-colors hover:text-[#f45c24]"
                >
                  {showHours ? "Hide hours" : "See hours"}
                </button>
              </div>

              <Link
                href={siteConfig.orderUrl}
                prefetch={false}
                data-track="order_started"
                data-track-location="home_locations"
                className="inline-flex min-h-11 w-full items-center justify-center rounded-full bg-[#f45c24] px-6 py-2.5 text-sm font-semibold text-[#1a1a1a] transition-colors hover:bg-[#d94e1a] sm:w-auto"
              >
                Order online
              </Link>
            </div>

            {showHours ? (
              <div>
                <ul
                  id="home-locations-hours"
                  className="mt-3 space-y-1.5 text-sm"
                >
                  {WEEKLY_HOURS.map((entry, index) => {
                    const isToday = index === todayIndex;
                    return (
                      <li
                        key={entry.day}
                        className="flex justify-between gap-6"
                      >
                        <span
                          className={
                            isToday
                              ? "font-semibold text-[#1a1a1a]"
                              : "text-neutral-500"
                          }
                        >
                          {entry.day}
                          {isToday ? (
                            <span className="sr-only"> (Today)</span>
                          ) : null}
                        </span>
                        <span
                          className={
                            isToday
                              ? "font-semibold tabular-nums text-[#1a1a1a]"
                              : "tabular-nums text-neutral-700"
                          }
                        >
                          {entry.hours}
                        </span>
                      </li>
                    );
                  })}
                </ul>
                <p className="mt-2 text-xs text-neutral-500">
                  Happy hour daily 4-6 PM
                </p>
              </div>
            ) : null}

            <div className="flex flex-col gap-2 lg:hidden">
              <Link
                href={mapsHref}
                target="_blank"
                rel="noopener noreferrer"
                data-track="directions_clicked"
                data-track-location="home_locations_directions"
                className="text-sm font-semibold text-neutral-600 transition-colors hover:text-[#f45c24]"
              >
                Get directions
              </Link>
              <Link
                href={siteConfig.reservationUrl}
                prefetch={false}
                data-track="reservation_clicked"
                data-track-location="home_locations_secondary"
                className="text-sm font-semibold text-neutral-600 transition-colors hover:text-[#f45c24]"
              >
                Reserve a table
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default OwnerLocations;
